import { FC } from "react";
import { CheckCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Card, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const SuccessPage: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleInstallClick = () => {
    // Replace with your navigation logic
    console.log("Navigate to /redirector");
    navigate("/redirector");
  };

  return (
    <div style={styles.container}>
      {/* Background elements */}
      <div style={styles.backgroundElement1}></div>
      <div style={styles.backgroundElement2}></div>
      
      <Card style={styles.card} bordered={false}>
        {/* Success Icon */}
        <div style={styles.iconContainer}>
          <div style={styles.iconWrapper}>
            <CheckCircleOutlined style={styles.checkIcon} />
          </div>
        </div>

        {/* Success Message */}
        <div style={styles.content}>
          <Title level={3} style={styles.title}>
            {t("success.title")}
          </Title>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleInstallClick}
            style={styles.button}
          >
            {t("success.buttons.install")}
          </Button>


        </div>
      </Card>
      
      {/* Floating particles */}
      <div style={styles.particle1}></div>
      <div style={styles.particle2}></div>
      <div style={styles.particle3}></div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 50%, #f3e8ff 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // padding: '12px',
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },
  
  backgroundElement1: {
    position: 'absolute' as const,
    top: '-160px',
    right: '-160px',
    width: '320px',
    height: '320px',
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)',
    borderRadius: '50%',
    filter: 'blur(60px)',
    pointerEvents: 'none' as const,
  },
  
  backgroundElement2: {
    position: 'absolute' as const,
    bottom: '-160px',
    left: '-160px',
    width: '320px',
    height: '320px',
    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
    borderRadius: '50%',
    filter: 'blur(60px)',
    pointerEvents: 'none' as const,
  },
  
  card: {
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center' as const,
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    padding: '32px 24px',
  },
  
  iconContainer: {
    position: 'relative' as const,
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  content :{

  },
  iconWrapper: {
    width: '80px',
    height: '80px',
    background: 'linear-gradient(135deg, #4ade80 0%, #10b981 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
    position: 'relative' as const,
  },
  
  checkIcon: {
    fontSize: '36px',
    color: 'white',
  },
  
  starIcon: {
    position: 'absolute' as const,
    top: '-4px',
    right: '-4px',
    fontSize: '20px',
    color: '#fbbf24',
    animation: 'pulse 2s infinite',
  },
  
  title: {
    background: 'linear-gradient(135deg, #1f2937 0%, #4b5563 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '16px !important',
    fontSize: '28px !important',
    fontWeight: '700 !important',
  },
  
  description: {
    color: '#6b7280',
    fontSize: '16px',
    lineHeight: '1.6',
    display: 'block',
    marginBottom: '32px',
  },
  
  button: {
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
    border: 'none',
    fontSize: '16px',
    fontWeight: '500',
    boxShadow: '0 8px 20px rgba(37, 99, 235, 0.3)',
    marginBottom: '16px',
    transition: 'all 0.3s ease',
  },
  
  footerText: {
    color: '#9ca3af',
    fontSize: '12px',
    opacity: 0.8,
  },
  
  particle1: {
    position: 'absolute' as const,
    top: '25%',
    left: '25%',
    width: '8px',
    height: '8px',
    background: '#93c5fd',
    borderRadius: '50%',
    animation: 'ping 3s infinite',
    opacity: 0.4,
  },
  
  particle2: {
    position: 'absolute' as const,
    top: '75%',
    right: '25%',
    width: '12px',
    height: '12px',
    background: '#c4b5fd',
    borderRadius: '50%',
    animation: 'pulse 4s infinite',
    opacity: 0.5,
  },
  
  particle3: {
    position: 'absolute' as const,
    bottom: '25%',
    left: '33%',
    width: '4px',
    height: '4px',
    background: '#a78bfa',
    borderRadius: '50%',
    animation: 'bounce 2s infinite',
    opacity: 0.6,
  },
};

// Add keyframes for animations
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
  @keyframes ping {
    75%, 100% {
      transform: scale(2);
      opacity: 0;
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% {
      transform: translateY(0);
    }
    40%, 43% {
      transform: translateY(-8px);
    }
    70% {
      transform: translateY(-4px);
    }
  }
`;
document.head.appendChild(styleSheet);

export default SuccessPage;